import React from 'react';
import Icon from '../../../components/AppIcon';
import AppImage from '../../../components/AppImage';

const LeaderboardTable = ({ data, currentUserId }) => {
  const getRankBadgeColor = (rank) => {
    if (rank === 1) return 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-white';
    if (rank === 2) return 'bg-gradient-to-br from-gray-300 to-gray-500 text-white';
    if (rank === 3) return 'bg-gradient-to-br from-orange-400 to-orange-600 text-white';
    return 'bg-muted text-muted-foreground';
  };

  const getChangeIcon = (change) => {
    if (change === 'up') return { icon: 'TrendingUp', color: 'var(--color-success)' };
    if (change === 'down') return { icon: 'TrendingDown', color: 'var(--color-error)' };
    return { icon: 'Minus', color: 'var(--color-muted-foreground)' };
  };

  if (!data || data?.length === 0) {
    return (
      <div className="p-8 text-center">
        <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
        <p className="text-muted-foreground">No leaderboard data available yet.</p>
        <p className="text-sm text-muted-foreground mt-2">Complete workouts to appear on the leaderboard!</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      {/* Desktop View */}
      <table className="hidden md:table w-full">
        <thead className="bg-muted/50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Rank
            </th>
            <th className="px-6 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Member
            </th>
            <th className="px-6 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Performance
            </th>
            <th className="px-6 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Details
            </th>
            <th className="px-6 py-3 text-center text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Trend
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {data?.map((entry) => {
            const changeConfig = getChangeIcon(entry?.change);
            const isCurrentUser = entry?.userId === currentUserId;

            return (
              <tr
                key={entry?.userId}
                className={`
                  transition-smooth hover:bg-muted/30
                  ${isCurrentUser ? 'bg-primary/5 border-l-4 border-l-primary' : ''}
                `}
              >
                <td className="px-6 py-4">
                  <div className={`
                    inline-flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm
                    ${getRankBadgeColor(entry?.rank)}
                  `}>
                    {entry?.rank <= 3 ? (
                      <Icon name="Crown" size={16} color="currentColor" />
                    ) : (
                      entry?.rank
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      {entry?.avatar ? (
                        <AppImage
                          src={entry?.avatar}
                          alt={`${entry?.name} avatar`}
                          className="w-10 h-10 rounded-full object-cover border-2 border-border"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center border-2 border-border">
                          <Icon name="User" size={20} color="var(--color-primary)" />
                        </div>
                      )}
                      {entry?.rank <= 3 && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                          <span className="text-xs">🏆</span>
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">
                        {entry?.name}
                        {isCurrentUser && (
                          <span className="ml-2 text-xs text-primary font-normal">(You)</span>
                        )}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-foreground">{entry?.primaryStat}</p>
                    <p className="text-xs text-muted-foreground">{entry?.primaryLabel}</p>
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <div>
                    <p className="text-sm font-medium text-foreground">{entry?.secondaryStat}</p>
                    <p className="text-xs text-muted-foreground">{entry?.tertiaryStats}</p>
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <div className="flex items-center justify-center">
                    <Icon name={changeConfig?.icon} size={20} color={changeConfig?.color} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* Mobile View */}
      <div className="md:hidden divide-y divide-border">
        {data?.map((entry) => {
          const changeConfig = getChangeIcon(entry?.change);
          const isCurrentUser = entry?.userId === currentUserId;

          return (
            <div
              key={entry?.userId}
              className={`
                p-4 transition-smooth
                ${isCurrentUser ? 'bg-primary/5 border-l-4 border-l-primary' : ''}
              `}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`
                  flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm flex-shrink-0
                  ${getRankBadgeColor(entry?.rank)}
                `}>
                  {entry?.rank <= 3 ? (
                    <Icon name="Crown" size={16} color="currentColor" />
                  ) : (
                    entry?.rank
                  )}
                </div>
                <div className="relative flex-shrink-0">
                  {entry?.avatar ? (
                    <AppImage
                      src={entry?.avatar}
                      alt={`${entry?.name} avatar`}
                      className="w-12 h-12 rounded-full object-cover border-2 border-border"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center border-2 border-border">
                      <Icon name="User" size={24} color="var(--color-primary)" />
                    </div>
                  )}
                  {entry?.rank <= 3 && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 rounded-full flex items-center justify-center">
                      <span className="text-xs">🏆</span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-foreground truncate">
                    {entry?.name}
                    {isCurrentUser && (
                      <span className="ml-2 text-xs text-primary font-normal">(You)</span>
                    )}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <Icon name={changeConfig?.icon} size={14} color={changeConfig?.color} />
                    <span className="text-xs text-muted-foreground">Trend</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3 bg-muted/30 rounded-lg p-3">
                <div className="text-center">
                  <p className="text-xl font-bold text-foreground">{entry?.primaryStat}</p>
                  <p className="text-xs text-muted-foreground">{entry?.primaryLabel}</p>
                </div>
                <div className="text-center border-l border-r border-border">
                  <p className="text-sm font-medium text-foreground">{entry?.secondaryStat}</p>
                  <p className="text-xs text-muted-foreground">Details</p>
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium text-foreground">{entry?.tertiaryStats}</p>
                  <p className="text-xs text-muted-foreground">Stats</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeaderboardTable;